import * as React from 'react';
import { Navigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  redirectTo?: string;
}

/**
 * RoleBasedRoute component that protects routes based on user roles.
 * 
 * @param children - The component to render if user has access
 * @param allowedRoles - Array of roles that can access this route. If undefined, all authenticated users can access.
 * @param redirectTo - Path to redirect to if user doesn't have access. Defaults to home page.
 */
export default function RoleBasedRoute({ 
  children, 
  allowedRoles,
  redirectTo = '/'
}: RoleBasedRouteProps) {
  const { user } = useAuth();

  // If no specific roles are required, allow all authenticated users
  if (!allowedRoles || allowedRoles.length === 0) {
    return <>{children}</>;
  }

  // Check if user's role is in the allowed roles
  if (user && allowedRoles.includes(user.role)) {
    return <>{children}</>;
  }

  // User doesn't have access, redirect
  return <Navigate to={redirectTo} replace />;
}

